import {
  cacheKeys,
  computeCommonGroups,
  computeGroupChanges,
  FeatureName,
  Group,
  PermissionValues,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentAccount,
  useCurrentWorkspace,
  useCustomMutation,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import {
  ColumnData,
  ListCountProvider,
  SelectionStatus,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AccountDetailPanel, PeopleDetailAccount } from './accountDetailPanel';
import { Accounts as AccountsOld } from './accounts-old';
import { AccountsComponent, SelectedAccountRow } from './accountsComponent';
import { AccountsFilters } from './accountsFilters';
import { AccountsGroupsPicker } from './accountsGroupsPicker';
import { AccountsSelectionPanelHeaderActions } from './accountsSelectionPanel';

export const Accounts = () => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnabled) {
    return <AccountsBase />;
  }

  return <AccountsOld />;
};

export const AccountsBase = ({
  rightColumn: rightColumnOverride,
  viewMode: viewModeOverride,
}: {
  rightColumn?: ColumnData;
  viewMode?: ViewMode;
} = {}) => {
  const { t, i18n } = useTranslation();
  const { workspaceId: paramWorkspaceId } = useParams();
  const workspace = useCurrentWorkspace(true);
  const queryClient = useQueryClient();
  const { createGroup } = useCreateGroup();
  const { account: currentAccount } = useCurrentAccount();
  const isGlobalAdmin = useMemo(
    () => !!currentAccount?.hasPermission(PermissionValues.globalAdmin),
    [currentAccount]
  );

  const [rowsSelected, setRowsSelected] = useState<SelectedAccountRow[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');
  const [selectedAccount, setSelectedAccount] = useState<
    PeopleDetailAccount | undefined
  >();

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );

  const currentWorkspaceId = paramWorkspaceId ?? workspace?.id;

  const { groups: availableGroups } = useAllWorkspaceGroups(currentWorkspaceId);
  const { mutateAsync: updateAccountsGroups } = useCustomMutation({
    query: UPDATE_ACCOUNTS_GROUPS,
  });
  const { mutateAsync: clearGroups } = useCustomMutation({
    query: CLEAR_GROUPS,
  });

  useEffect(() => {
    if (rowsSelected.length === 0) {
      setSelectedGroups([]);
      return;
    }

    const nextSelected = computeCommonGroups(
      rowsSelected,
      (row) => row.groups ?? [],
      availableGroups
    ).map((g) => ({ id: g.id, name: g.name }));

    setSelectedGroups(nextSelected);
  }, [rowsSelected, availableGroups]);

  useEffect(() => {
    if (rowsSelected.length > 0) {
      setSelectedAccount(undefined);
    }
  }, [rowsSelected.length]);

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
    rightColumnOverride?.onBackButtonPress?.();
  }, [rightColumnOverride]);

  const handleGroupsChange = useCallback(
    async (nextGroups: Group[]) => {
      const { added, removed } = computeGroupChanges(
        selectedGroups,
        nextGroups
      );

      setSelectedGroups(nextGroups);

      if (!currentWorkspaceId) {
        return;
      }

      await updateAccountsGroups({
        input: {
          addedGroups: added.map((g) => ({ id: g.id })),
          removedGroups: removed.map((g) => ({ id: g.id })),
          accountIds: rowsSelectedIds,
          workspaceId: currentWorkspaceId,
        },
      });

      await queryClient.invalidateQueries({
        queryKey: cacheKeys('account').all,
      });
    },
    [
      selectedGroups,
      rowsSelectedIds,
      currentWorkspaceId,
      updateAccountsGroups,
      queryClient,
    ]
  );

  const handleReset = useCallback(async () => {
    if (rowsSelectedIds.length === 0) return;
    await clearGroups({ input: { accountIds: rowsSelectedIds } });
    await queryClient.invalidateQueries({
      queryKey: cacheKeys('account').all,
    });
    clearSelection();
  }, [rowsSelectedIds, clearGroups, queryClient, clearSelection]);

  const onRowClick = useCallback((row: PeopleDetailAccount) => {
    if (!row?.id) return;
    setSelectedAccount(row);
  }, []);

  const [count, setCount] = useState<number>();
  const leftColumn = {
    title: 'people.menu.people',
    count,
    mainComponent: (
      <AccountsComponent
        onRowsSelectedChange={setRowsSelected}
        selectedRowIds={rowsSelectedIds}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
        onItemsCountChange={setCount}
        onRowClick={onRowClick}
      />
    ),
    headerComponent: <AccountsFilters />,
    disableScroll: true,
  };

  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (rowsSelected.length === 0 || !currentWorkspaceId) {
      return undefined;
    }

    const title =
      rowsSelected.length === 1
        ? t('account.topBar.personSelected')
        : t('account.topBar.peopleSelected', { count: rowsSelected.length });

    // SlideLayout re-runs `t()` on whatever title we pass. Register the
    // already-translated string as a resource so that second pass resolves
    // cleanly instead of logging `missingKey`.
    i18n.addResource(i18n.language, 'translation', title, title);

    return {
      title,
      mainComponent: (
        <AccountsGroupsPicker
          selectedGroups={selectedGroups}
          onGroupsChange={handleGroupsChange}
          onCreateGroup={createGroup}
          workspaceId={currentWorkspaceId}
        />
      ),
      headerComponent: (
        <AccountsSelectionPanelHeaderActions onReset={handleReset} />
      ),
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [
    rowsSelected,
    currentWorkspaceId,
    selectedGroups,
    handleGroupsChange,
    handleReset,
    clearSelection,
    createGroup,
    t,
    i18n,
  ]);

  const detailRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!selectedAccount || !currentWorkspaceId) return undefined;
    const displayName =
      [selectedAccount.name?.first, selectedAccount.name?.last]
        .filter(Boolean)
        .join(' ') ||
      selectedAccount.email ||
      '';
    return {
      title: '',
      rawTitle: displayName,
      mainComponent: (
        <AccountDetailPanel
          key={selectedAccount.id}
          account={selectedAccount}
          workspaceId={currentWorkspaceId}
          showGlobalAdminRole={isGlobalAdmin}
          onRemoved={() => setSelectedAccount(undefined)}
        />
      ),
      showBackButton: true,
      onBackButtonPress: () => setSelectedAccount(undefined),
      disableScroll: true,
    };
  }, [selectedAccount, currentWorkspaceId, isGlobalAdmin]);

  const resolvedRightColumn =
    selectionRightColumn ?? detailRightColumn ?? rightColumnOverride;
  const resolvedViewMode =
    viewModeOverride ??
    (selectionRightColumn || detailRightColumn || rightColumnOverride
      ? ViewMode.DUAL
      : ViewMode.SINGLE);

  return (
    <ListCountProvider count={count}>
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={resolvedRightColumn}
        viewMode={resolvedViewMode}
      />
    </ListCountProvider>
  );
};

const UPDATE_ACCOUNTS_GROUPS = `
  mutation Account($input: UpdateAccountsGroupsInput!) {
    account {
      updateAccountsGroups(input: $input) {
        success
        message
      }
    }
  }
`;

const CLEAR_GROUPS = `
  mutation ClearGroups($input: ClearGroupsInput!) {
    account {
      clearGroups(input: $input) {
        success
      }
    }
  }
`;
